import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, CalendarClock, ArrowRight } from "lucide-react";
import { UserAvatar, Stars, VerifiedBadge, Pill } from "@/components/ui-bits";
import { fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

export function ExpertCard({ expert, to }) {
  const { t } = useTranslation();
  const nextSlot = (expert.availability || []).find((s) => !s.booked && !s.busy && new Date(s.start) > new Date());
  const href = to || `/experts/${expert.expert_id}`;
  return (
    <Link
      to={href}
      data-testid={`expert-card-${expert.expert_id}`}
      className="group card-milled rounded-[0.7rem] p-5 flex flex-col transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
    >
      <div className="flex items-start gap-3.5">
        <UserAvatar src={fileUrl(expert.picture)} name={expert.name} size={52} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-[15px] text-foreground truncate">{expert.name}</h3>
            {expert.verified && <VerifiedBadge label="" className="shrink-0" />}
          </div>
          <p className="text-sm text-muted-foreground truncate">{expert.title}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground leading-relaxed mt-3.5 line-clamp-2">{expert.headline}</p>

      <div className="mt-3.5 flex flex-wrap gap-1.5">
        {(expert.fields || []).slice(0, 2).map((f) => (
          <Pill key={f} className="bg-primary/10 text-primary">{f}</Pill>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <Stars value={expert.rating_avg} count={expert.rating_count} />
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin size={13} strokeWidth={1.75} /> {expert.location || t("emp.card.remote")}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        {nextSlot ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
            <CalendarClock size={14} strokeWidth={1.75} /> {t("emp.card.nextSlot", { date: fmtDate(nextSlot.start) })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("emp.card.noSlots")}</span>
        )}
        <span className="inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-1.5 transition-all">
          {t("emp.card.view")} <ArrowRight size={15} strokeWidth={2} />
        </span>
      </div>
    </Link>
  );
}
